import { client } from 'clients/eyekia';
import { action, makeObservable, observable, runInAction } from 'mobx';
import { Image, Asset, Primitive, DefaultInteractionType } from '@sophya/eyekia';
import { AssetDO } from './Asset';
import { BaseStore } from './BaseStore';
import { ImageDO } from './Image';
import { OrientationDO, OrientationKey } from './Orientation';
import { PrimitiveDO } from './Primitive';
import { FabricInterface, RootStore } from './RootStore';

export class AssetStore extends BaseStore {
  assets = new Map<string, AssetDO>();
  assetsByCategory = new Map<string, string[]>();

  constructor(private fabric: FabricInterface, private root: RootStore) {
    super();
    makeObservable(this, {
      assets: observable,
      loadingAssets: observable,
      currentAsset: observable,
      setCurrentAsset: action,
      currentCategoryId: observable,
      assetsByCategory: observable,
    });

    fabric.setImageUpdateFn(this.handleFabricUpdateImage);
    fabric.setPrimUpdateFn(this.handleFabricUpdatePrimititve);
    fabric.setSelectFn(this.handleFabricSelect);

    document.addEventListener('keydown', this.handleKeyDown);
    this.keyDisposer = () => document.removeEventListener('keydown', this.handleKeyDown);

    this.handleLoadAssets('null');
  }

  keyDisposer: () => void;

  handleKeyDown = (e: KeyboardEvent) => {
    switch (e.key) {
      case 'q': {
        this.getCurrentAsset()?.cycleOrientation();
        break;
      }
      case 'w': {
        this.getCurrentOrientation()?.cycleImage();
        break;
      }
      case 'e': {
        this.getCurrentImage()?.cyclePrimitive();
        break;
      }
    }
  };

  loadingAssets: boolean = false;

  handleLoadAssets = async (categoryId: string) => {
    // Skip if already loaded
    if (this.assetsByCategory.has(categoryId)) {
      console.log('Assets for category', categoryId, 'already loaded, skipping');
      this.currentCategoryId = categoryId;
      return;
    }

    this.loadingAssets = true;
    console.log('Loading assets for category:', categoryId);
    const res = await client().v1.draftAssetList({ categoryId });
    runInAction(() => {
      const assetIds = [];
      for (const resAsset of res.data.assets) {
        const { id, data } = resAsset;
        assetIds.push(id);
        const asset = new AssetDO(id, this.fabric, this);
        //Rudolf - ignoring 'deep type instance' for Asset
        //@ts-ignore
        asset.updateFromSchema(data as Asset);
        this.assets.set(id, asset);
      }
      this.assetsByCategory.set(categoryId, assetIds);
      this.currentCategoryId = categoryId;
      console.log('Loaded', assetIds.length, 'assets for category:', categoryId);
      this.loadingAssets = false;
    });
  };

  // Load a single asset directly via the authenticated draft endpoint
  private handleLoadSingleAsset = async (assetId: string): Promise<AssetDO | null> => {
    try {
      console.log('🚀 Loading single asset directly:', assetId);

      const res = await client().v1.draftAssetDetail(assetId);
      const asset = new AssetDO(assetId, this.fabric, this);
      //@ts-ignore
      asset.updateFromSchema(res.data as Asset);
      runInAction(() => {
        this.assets.set(assetId, asset);
      });
      console.log('✅ Successfully loaded single asset:', assetId);
      return asset;
    } catch (error) {
      console.log('❌ Single asset loading failed:', error.message || error);
      return null;
    }
  };

  handleLoadAssetById = async (assetId: string, forceRefresh: boolean = false): Promise<AssetDO | null> => {
    // Optionally bypass cache to refresh URLs/data
    const existingAsset = this.assets.get(assetId);

    try {
      console.log('🔍 Loading asset by ID:', assetId);
      
      // First try to load just this single asset (ensures canonical URLs)
      if (forceRefresh || !existingAsset) {
        const singleAsset = await this.handleLoadSingleAsset(assetId);
        if (singleAsset) {
          return singleAsset;
        }
      }

      console.log('🔄 Single asset loading failed, falling back to category-based loading');

      // Check if asset exists in already loaded categories first
      for (const [categoryId, assetIds] of this.assetsByCategory.entries()) {
        if (assetIds.includes(assetId)) {
          const asset = this.assets.get(assetId);
          if (asset) {
            console.log('✅ Found asset', assetId, 'in already loaded category:', categoryId);
            return asset;
          }
        }
      }

      // If not found in loaded categories, try loading the 'null' category first
      if (!this.assetsByCategory.has('null')) {
        console.log('📥 Loading null category to search for asset:', assetId, '(this will load ~93 assets but only happens once per session)');
        await this.handleLoadAssets('null');
        const asset = this.assets.get(assetId);
        if (asset) {
          console.log('✅ Found asset', assetId, 'in null category');
          return asset;
        }
      }

      // If asset not found in null category, try other categories one by one
      const categories = Array.from(this.root.categories.categories.keys());

      for (const categoryId of categories) {
        if (categoryId !== 'null' && !this.assetsByCategory.has(categoryId)) {
          console.log('Searching for asset', assetId, 'in category:', categoryId);
          await this.handleLoadAssets(categoryId);
          const asset = this.assets.get(assetId);
          if (asset) {
            console.log('Found asset', assetId, 'in category:', categoryId);
            return asset;
          }
        }
      }

      // Asset not found in any category
      console.warn('Asset not found in any category:', assetId);
      return existingAsset ?? null;
    } catch (error) {
      console.error('Failed to load asset by ID:', assetId, error);
      return existingAsset ?? null;
    }
  };

  currentCategoryId: string;

  currentAsset: string;

  setCurrentAsset = (assetId: string) => {
    this.currentAsset = assetId;
  };

  getCategories = () => {
    return this.root.categories.keys();
  };

  getCategoryInteraction = (categoryId: string): DefaultInteractionType | null => {
    return this.root.categories.get(categoryId)?.defaultInteraction.value as DefaultInteractionType | null;
  }

  getCategoryName = (categoryId: string): string => {
    return this.root.categories.get(categoryId)?.name.value as string;
  }

  handleCreateAsset = async (): Promise<void> => {
    const assetRes = await client().v1.assetCreate({});
    const id = assetRes.data.assetId;
    runInAction(() => {
      this.assets.set(id, new AssetDO(id, this.fabric, this));
    });
  };

  handlePublishAll = async (): Promise<void> => {
    const categoryAssetIds = this.assetsByCategory.get(this.currentCategoryId);
    const publishTasks = categoryAssetIds.map((assetId) => {
      const assetData = this.getAsset(assetId);
        if (!assetData.published.value || assetData.modifiedSinceLastPublish) {
          return assetData.publishAsset();
        }
    });
    await Promise.all(publishTasks);
  };

  handleSaveAll = async (): Promise<void> => {
    const categoryAssetIds = this.assetsByCategory.get(this.currentCategoryId) ?? [];
    const saveTasks = categoryAssetIds
      .map(assetId => this.getAsset(assetId))
      .filter((asset): asset is AssetDO => !!asset)
      .map(asset => asset.networkUpdateAsset());
    await Promise.all(saveTasks);
  };

  handleFabricUpdateImage = (id: string, data: Image) => {
    const asset = this.getAsset(this.currentAsset);
    const orientation = asset?.getOrientation(asset?.currentOrientation);
    const image = orientation?.getImage(id);

    image?.updateFromSchema(data);
    asset?.save();
  };

  handleFabricUpdatePrimititve = (id: string, data: Primitive) => {
    const asset = this.getAsset(this.currentAsset);
    const orientation = asset?.getOrientation(asset?.currentOrientation);
    const image = orientation?.getImage(orientation.currentImage);
    const prim = image?.getPrimitive(id);

    prim?.updateFromSchema(data);
    asset?.save();
  };

  handleFabricSelect = (imageId: string, primitiveID: string) => {
    const asset = this.getAsset(this.currentAsset);
    const orientation = asset?.getOrientation(asset?.currentOrientation);

    orientation?.selectImage(imageId);
  };

  handleDispose(): void {
    this.keyDisposer?.();
    for (const asset of this.assets.values()) {
      asset.dispose();
    }
  }

  getAsset(assetId: string): AssetDO | undefined {
    return this.assets.get(assetId);
  }

  getCurrentAsset(): AssetDO | undefined {
    return this.assets.get(this.currentAsset);
  }

  deleteAsset(id: string): void {
    runInAction(() => {
      const asset = this.assets.get(id);
      let cat = asset?.category?.value;
      if (cat === null) {
        cat = 'null';
      }
      const assetsCategory = this.assetsByCategory.get(cat);
      if (assetsCategory) {
        console.debug('removing asset ', id, ' from cat ', cat, assetsCategory, this);
        this.assetsByCategory.set(
          cat,
          assetsCategory.filter(value => value !== id),
        );
      }
      this.assets.delete(id);
    });
    this.networkDeleteAsset(id);
  }
  networkDeleteAsset = async (id: string): Promise<void> => {
    await client().v1.assetDelete(id);
  };

  getOrientation(assetId: string, orientationKey: OrientationKey): OrientationDO | undefined {
    return this.getAsset(assetId)?.getOrientation(orientationKey);
  }

  getNextOrientation(assetId: string, orientationKey: OrientationKey): OrientationDO | undefined {
    return this.getAsset(assetId)?.getNextOrientation(orientationKey);
  }

  getBestOrientation(assetId: string, orientationKey: OrientationKey): OrientationDO | undefined {
    return this.getAsset(assetId)?.getBestOrientation(orientationKey);
  }

  getCurrentOrientation(): OrientationDO | undefined {
    const asset = this.getCurrentAsset();
    return asset?.getOrientation(asset?.currentOrientation);
  }

  getImage(assetId: string, orientationKey: OrientationKey, imageId: string): ImageDO | undefined {
    return this.getOrientation(assetId, orientationKey)?.getImage(imageId);
  }

  getCurrentImage(): ImageDO | undefined {
    const orientation = this.getCurrentOrientation();
    return orientation?.getImage(orientation?.currentImage);
  }

  getPrimitive(
    assetId: string,
    orientationKey: OrientationKey,
    imageId: string,
    primitiveId: string,
  ): PrimitiveDO | undefined {
    return this.getImage(assetId, orientationKey, imageId)?.getPrimitive(primitiveId);
  }

  getCurrentPrimitive(): PrimitiveDO | undefined {
    const image = this.getCurrentImage();
    return image?.getCurrentPrimitive();
  }
}
